import { StrictMode, createRef, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { AuthProvider } from 'react-oidc-context';
import { Provider as ReduxProvider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider, CssBaseline, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { SnackbarProvider, useSnackbar, type SnackbarKey } from 'notistack';

import './index.css';
import './i18n';

import i18n from './i18n';
import { theme } from '@/theme/theme';
import { store } from '@/store';
import { oidcConfig } from '@/auth/oidcConfig';
import { setApiToastHandler, setApiTranslator } from '@/api/axios';
import { AuthGate } from '@/auth/AuthGate';
import { AppRouter } from '@/AppRouter';

// Tiny side-effect-only component that wires the notistack `enqueueSnackbar`
// callback into the axios interceptor. Kept inside the SnackbarProvider so
// `useSnackbar()` works.
const ApiToastBridge = (): null => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  useEffect(() => {
    setApiToastHandler((message, severity) => {
      enqueueSnackbar(message, { variant: severity });
    });
    setApiTranslator((key) => t(key));
    return () => setApiToastHandler(null);
  }, [enqueueSnackbar, t]);
  return null;
};

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found');

// Notistack v3 hides only on `autoHideDuration`. The user needs to be able
// to manually dismiss any toast immediately — wire a close IconButton via
// the provider-level `action` prop. Ref-based dismiss avoids each toast
// having to forward its own `closeSnackbar` instance.
const notistackRef = createRef<SnackbarProvider>();
const closeAction = (key: SnackbarKey) => (
  <IconButton
    size="small"
    aria-label="Zavřít"
    onClick={() => notistackRef.current?.closeSnackbar(key)}
    sx={{ color: 'inherit' }}
  >
    <CloseIcon fontSize="small" />
  </IconButton>
);

createRoot(rootEl).render(
  <StrictMode>
    <I18nextProvider i18n={i18n}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SnackbarProvider
          ref={notistackRef}
          maxSnack={3}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          autoHideDuration={5000}
          action={closeAction}
        >
          <ReduxProvider store={store}>
            <BrowserRouter>
              <AuthProvider {...oidcConfig}>
                <ApiToastBridge />
                <AuthGate>
                  <AppRouter />
                </AuthGate>
              </AuthProvider>
            </BrowserRouter>
          </ReduxProvider>
        </SnackbarProvider>
      </ThemeProvider>
    </I18nextProvider>
  </StrictMode>,
);
